import React, { useMemo } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { API } from '@luxuryescapes/lib-types';

import CopyableField from '~/components/Common/CopyableField';
import GridPagination from '~/components/Common/Elements/GridPagination';

import currencyFormatter from '~/utils/currencyFormatter';

import { OrderStatusChip } from './AllOrders/components/OrderStatusChip';
import { dateFormatter } from './formatters';

const EMPTY_VALUE = '—';

type FlightOrderListApiResponse = Omit<API.Order.Order, 'flight_items' | 'offline_flight_items'> & {
  request_type?: string | null;
  flight_items?: Array<API.Order.FlightItem & { pnr_id?: string; provider?: string }>;
  offline_flight_items?: Array<{
    pnr_ids?: Array<string>;
    provider?: 'offline';
    request_type?: 'New' | 'Change' | 'Mixed' | null;
    total?: number;
  }>;
};

type FlightOrderListRow = FlightOrderListApiResponse & {
  requestType: string;
  lePnr: string;
  provider: string;
  totalFlightPrice: string;
};

interface Props {
  orders: Array<FlightOrderListApiResponse>;
  total: number;
  page: number;
  sizePerPage: number;
  onPageChange: (page: number) => void;
  rerender: () => void;
}

function getLePnr(order: FlightOrderListApiResponse): string {
  const offlinePnrIds = order.offline_flight_items?.flatMap((item) => item.pnr_ids ?? []) ?? [];
  const onlinePnrIds = order.flight_items?.map((item) => item.pnr_id).filter(Boolean) ?? [];
  const pnrIds = offlinePnrIds.length > 0 ? offlinePnrIds : onlinePnrIds;

  return pnrIds.join(', ') || EMPTY_VALUE;
}

function getProvider(order: FlightOrderListApiResponse): string {
  const provider = order.flight_items?.find((item) => item.provider)?.provider;

  if (provider) {
    return provider === 'travelFusion' ? 'TravelFusion' : provider.charAt(0).toUpperCase() + provider.slice(1);
  }

  if (order.offline_flight_items?.length) {
    return 'Offline';
  }

  return EMPTY_VALUE;
}

function getRequestType(order: FlightOrderListApiResponse): string {
  const requestType = order.request_type ?? order.offline_flight_items?.find((item) => item.request_type)?.request_type;

  if (requestType === 'Change') {
    return 'Date change';
  }

  if (requestType === 'New') {
    return 'New booking';
  }

  if (requestType === 'Mixed') {
    return 'Mixed';
  }

  if (requestType) {
    return requestType;
  }

  if (order.flight_items?.length) {
    return 'New booking';
  }

  if (order.offline_flight_items?.length) {
    return 'Offline booking';
  }

  return EMPTY_VALUE;
}

function getNumericFlightTotal(total: API.Order.FlightItem['total'] | number | null | undefined): number | null {
  if (total == null || (typeof total === 'string' && total.trim() === '')) {
    return null;
  }

  const numericTotal = Number(total);

  return Number.isFinite(numericTotal) ? numericTotal : null;
}

function getTotalFlightPrice(order: FlightOrderListApiResponse): string {
  let flightTotal = 0;
  let hasFlightTotal = false;

  for (const item of order.flight_items ?? []) {
    const itemTotal = getNumericFlightTotal(item.total);

    if (itemTotal != null) {
      flightTotal += itemTotal;
      hasFlightTotal = true;
    }
  }

  for (const item of order.offline_flight_items ?? []) {
    const itemTotal = getNumericFlightTotal(item.total);

    if (itemTotal != null) {
      flightTotal += itemTotal;
      hasFlightTotal = true;
    }
  }

  if (!order.currency_code || !hasFlightTotal) {
    return EMPTY_VALUE;
  }

  return currencyFormatter(order.currency_code, flightTotal, 2, 'code').replace(/\u00a0/g, ' ');
}

export function getFlightOrderListRow(order: FlightOrderListApiResponse): FlightOrderListRow {
  return {
    ...order,
    requestType: getRequestType(order),
    lePnr: getLePnr(order),
    provider: getProvider(order),
    totalFlightPrice: getTotalFlightPrice(order),
  };
}

function orderEmailLinkFormatter({ row }: GridRenderCellParams<FlightOrderListRow>) {
  return (
    <Link
      href={`/purchases/${row.id}`}
      target="_blank"
      rel="noreferrer"
      title="View order details in new window"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {row.customer_email}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </Link>
  );
}

const columns: Array<GridColDef<FlightOrderListRow>> = [
  {
    field: 'customer_email',
    headerName: 'Customer Email',
    sortable: false,
    width: 210,
    renderCell: orderEmailLinkFormatter,
    display: 'flex',
  },
  { field: 'customer_full_name', headerName: 'Customer Name', width: 150, sortable: false, display: 'flex' },
  {
    field: 'created_at',
    headerName: 'Order Placed',
    sortable: false,
    width: 180,
    renderCell: (params) => dateFormatter(params.value),
    display: 'flex',
  },
  {
    field: 'status',
    headerName: 'Order Status',
    width: 130,
    sortable: false,
    display: 'flex',
    renderCell: (params) => <OrderStatusChip orderStatus={params.row.status} />,
  },
  {
    field: 'requestType',
    headerName: 'Request Type',
    width: 130,
    sortable: false,
    display: 'flex',
  },
  {
    field: 'lePnr',
    headerName: 'LE PNR',
    sortable: false,
    width: 150,
    renderCell: ({ row }) =>
      row.lePnr === EMPTY_VALUE ? (
        EMPTY_VALUE
      ) : (
        <CopyableField value={row.lePnr} tooltip={`Copy LE PNR ${row.lePnr}`} />
      ),
    display: 'flex',
  },
  { field: 'provider', headerName: 'Provider', width: 110, sortable: false, display: 'flex' },
  {
    field: 'totalFlightPrice',
    headerName: 'Total Flight Price',
    width: 150,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    display: 'flex',
  },
];

export default function FlightsPage({ orders, total, sizePerPage, onPageChange, page }: Props) {
  const rows = useMemo(() => orders.map(getFlightOrderListRow), [orders]);

  return (
    <Stack sx={{ width: '100%', overflowX: 'auto' }}>
      <DataGrid
        rows={rows}
        rowCount={total}
        columns={columns}
        pagination
        paginationMode="server"
        paginationModel={{ page: page - 1, pageSize: sizePerPage }}
        pageSizeOptions={[sizePerPage]}
        onPaginationModelChange={({ page }) => onPageChange(page + 1)}
        slots={{ pagination: GridPagination }}
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        autoHeight
        sx={{ minWidth: 1210 }}
      />
    </Stack>
  );
}
